import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Activity, AlertTriangle, BarChart3, Eye, Gift, KeyRound, Loader2,
  Palette, RefreshCw, RotateCw, ShieldAlert, Ticket, Webhook,
} from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { formatMoney } from './lib/formatters';
import {
  BONUS_TYPES, CREDIT_STATUS_LABELS, CREDIT_STATUS_TONE, canRetryCredit,
  errorText, ignoredFields, partnerApi,
} from './lib/sportdigiPartner';
import {
  Busy, Empty, Field, IgnoredFields, JsonEditor, Panel, Problem,
} from './components/SportDigiBits';

/**
 * Digitain Sportsbook — self-servis yönetim ekranı (Partner API).
 *
 * Sağlayıcının **iki yüzeyi** vardır: para akışı (cüzdan callback'i, bahis,
 * cashout) ve yönetim. Birincisi kendiliğinden çalışır ve panelde yalnızca
 * ayar kartı olarak görünür (Oyun Kontrol → Spor & Cashout); bu ekran
 * ikincisidir — sağlayıcının kendi hub panelinde yapılan her şey buradan da
 * yapılabilir (EveryMatrix'teki EmSportsManager'ın aynısı).
 *
 * Ekranın var olma sebebi tek bir cümledir: **ödemesi ölü kalan kupon.**
 * Sonuç ödemesi bizim cüzdanımıza ulaşamazsa sağlayıcı on sekiz kez dener,
 * sonra `dead` işaretler ve kendiliğinden bir daha denemez. Kalan sekmeler
 * (bonus, risk, görünürlük, cashout, tema, trading, marka, webhook) aynı
 * yüzeyin öteki parçalarıdır ve hepsi sağlayıcının kaydına yazar: bizim
 * veritabanımızda karşılıkları **yoktur**, her sekme açıldığında okunur.
 */

const TABS = [
  { id: 'summary', label: 'Özet', icon: BarChart3 },
  { id: 'bets', label: 'Kuponlar', icon: Ticket },
  { id: 'bonus', label: 'Bonus & Freebet', icon: Gift },
  { id: 'risk', label: 'Risk & Limitler', icon: ShieldAlert },
  { id: 'visibility', label: 'Görünürlük & Cashout', icon: Eye },
  { id: 'appearance', label: 'Görünüm', icon: Palette },
  { id: 'trading', label: 'Trading & Markalar', icon: Activity },
  { id: 'webhooks', label: 'Webhook & Anahtar', icon: Webhook },
];

const CREDIT_FILTERS = [
  { value: '', label: 'Tümü' },
  { value: 'dead', label: 'Ölü ödeme' },
  { value: 'failed', label: 'Başarısız' },
  { value: 'pending', label: 'Kuyrukta' },
  { value: 'done', label: 'Ödendi' },
  { value: 'none', label: 'Ödeme yok' },
];

const number = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const Stat = ({ label, value, hint, tone = 'text-white' }) => (
  <div className="rounded-lg border border-slate-800 bg-slate-950/40 p-3">
    <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">
      {label}
    </div>
    <div className={`mt-1 text-lg font-bold ${tone}`}>{value}</div>
    {hint && <div className="mt-1 text-[10px] text-slate-500">{hint}</div>}
  </div>
);

// --------------------------------------------------------------------------- #
// Özet
// --------------------------------------------------------------------------- #
const SummaryTab = () => {
  const [state, setState] = useState({ loading: true });

  const load = useCallback(async () => {
    setState({ loading: true });
    try {
      const [summary, alerts, flags] = await Promise.all([
        partnerApi.summary(),
        partnerApi.alerts({ limit: 50 }).catch(() => ({ data: {} })),
        partnerApi.flags({ limit: 50 }).catch(() => ({ data: {} })),
      ]);
      setState({
        loading: false,
        summary: summary.data || {},
        alerts: alerts.data?.alerts || alerts.data?.items || [],
        flags: flags.data?.flags || flags.data?.items || [],
      });
    } catch (error) {
      setState({ loading: false, error: errorText(error) });
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (state.loading) return <Busy />;
  if (state.error) return <Problem>{state.error}</Problem>;

  const summary = state.summary || {};
  return (
    <div className="space-y-4">
      <Panel
        title="Sağlayıcıdaki özet"
        description={
          'Rakamlar sağlayıcının kendi defterinden gelir ve bizim '
          + 'raporlarımızdan ayrıdır: reddedilen bahisler sayılmaz, iptal '
          + 'edilenler hacme girmez. GGR = bahis − ödeme; komisyon bunun '
          + 'üzerinden Sportsbook cüzdanınızdan kesilir.'
        }
        actions={(
          <button
            type="button"
            className="admin-btn-secondary inline-flex items-center gap-2 !py-1 !text-[11px]"
            onClick={load}
          >
            <RefreshCw size={12} /> Yenile
          </button>
        )}
      >
        <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
          <Stat label="Kupon" value={number(summary.bet_count).toLocaleString('tr-TR')} hint={`Açık: ${number(summary.open_bets)}`} />
          <Stat label="Hacim" value={formatMoney(number(summary.turnover))} />
          <Stat label="Ödeme" value={formatMoney(number(summary.payout))} />
          <Stat
            label="GGR"
            value={formatMoney(number(summary.ggr))}
            tone={number(summary.ggr) < 0 ? 'text-rose-300' : 'text-emerald-300'}
            hint="Negatif GGR: kupon kazandı, cüzdan artar"
          />
        </div>
      </Panel>

      <Panel
        title="Uyarılar"
        description={
          'Sağlayıcının kendi uyarı defteri. Ödeme ölü kaldığında '
          + '(credit_dead) ve yanıtı alınamayan bir bahis geri alındığında '
          + '(debit_unknown) burada satır oluşur — ödeme olanlar Kuponlar '
          + 'sekmesinden çözülür.'
        }
      >
        {state.alerts?.length ? (
          <ul className="space-y-1">
            {state.alerts.slice(0, 30).map((row, index) => (
              <li
                key={row.alert_id || row.id || index}
                className="flex flex-wrap items-center gap-2 rounded border border-slate-800 bg-slate-950/40 p-2 text-[11px]"
              >
                <AlertTriangle
                  size={12}
                  className={row.severity === 'critical'
                    ? 'text-rose-400' : 'text-amber-300'}
                />
                <span className="text-slate-200">
                  {row.text || row.category || 'Uyarı'}
                </span>
                {row.bet_id && (
                  <span className="text-[10px] text-slate-500">
                    kupon #{row.bet_id}
                  </span>
                )}
              </li>
            ))}
          </ul>
        ) : (
          <Empty>Uyarı yok.</Empty>
        )}
      </Panel>

      <Panel
        title="İşaretlenen oyuncu ve maçlar"
        description={
          'İnceleme işareti bahsi **engellemez**: sağlayıcı yalnızca dikkat '
          + 'çeker. Eşikler Risk & Limitler sekmesindeki userFlags / '
          + 'matchFlags alanlarındadır.'
        }
      >
        {state.flags?.length ? (
          <ul className="space-y-1">
            {state.flags.slice(0, 30).map((row, index) => (
              <li
                key={row.id || index}
                className="rounded border border-slate-800 bg-slate-950/40 p-2 text-[11px] text-slate-300"
              >
                {row.text || row.reason || JSON.stringify(row).slice(0, 160)}
              </li>
            ))}
          </ul>
        ) : (
          <Empty>İşaretlenen kayıt yok.</Empty>
        )}
      </Panel>
    </div>
  );
};

// --------------------------------------------------------------------------- #
// Kuponlar
// --------------------------------------------------------------------------- #
const BetsTab = () => {
  const { toast } = useToast();
  const [creditStatus, setCreditStatus] = useState('dead');
  const [player, setPlayer] = useState('');
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [problem, setProblem] = useState('');
  const [busy, setBusy] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setProblem('');
    try {
      const params = { limit: 100 };
      if (creditStatus) params.credit_status = creditStatus;
      if (player.trim()) params.player = player.trim();
      const { data } = await partnerApi.bets(params);
      setRows(data?.bets || []);
      setTotal(number(data?.total));
    } catch (error) {
      setProblem(errorText(error, 'Kupon listesi alınamadı'));
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [creditStatus, player]);

  useEffect(() => { load(); }, [load]);

  const retry = async (row) => {
    const id = String(row.bet_id ?? row.id ?? '');
    setBusy(id);
    try {
      const { data } = await partnerApi.retryCredit(id);
      toast({
        title: 'Ödeme yeniden kuyruğa alındı',
        description:
          `Kupon #${id} · ${data?.previous_status || '—'} → `
          + `${data?.credit_status || 'pending'}. Ödeme ilk denemedeki `
          + 'kimlikle çıkar; bizde zaten işlenmişse tekrar sayılmaz.',
      });
      await load();
    } catch (error) {
      toast({
        title: 'Ödeme kuyruğa alınamadı',
        description: errorText(error),
        variant: 'destructive',
      });
    } finally {
      setBusy('');
    }
  };

  return (
    <div className="space-y-4">
      <Panel
        title="Sağlayıcıdaki kuponlar"
        description={
          'Bu liste sağlayıcının defteridir ve bizim Bahis Geçmişi '
          + 'ekranımızdan ayrıdır: oradaki satırlar para hareketinden doğar, '
          + 'buradaki satır ödemenin sağlayıcı tarafındaki durumunu taşır. '
          + 'İkisi yalnızca bir durumda ayrışır ve o durum buradan çözülür: '
          + 'ödemesi başarısız ya da ölü kalan kupon.'
        }
        actions={(
          <button
            type="button"
            className="admin-btn-secondary inline-flex items-center gap-2 !py-1 !text-[11px]"
            onClick={load}
          >
            <RefreshCw size={12} /> Yenile
          </button>
        )}
      >
        <div className="mb-3 flex flex-wrap items-end gap-2">
          <Field label="Ödeme durumu">
            <select
              className="admin-input admin-input-w-sm"
              value={creditStatus}
              onChange={(event) => setCreditStatus(event.target.value)}
            >
              {CREDIT_FILTERS.map((row) => (
                <option key={row.value} value={row.value}>{row.label}</option>
              ))}
            </select>
          </Field>
          <Field label="Oyuncu kimliği">
            <input
              className="admin-input admin-input-w-md"
              value={player}
              placeholder="external_player_id"
              onChange={(event) => setPlayer(event.target.value)}
            />
          </Field>
        </div>

        <Problem>{problem}</Problem>

        {loading ? <Busy /> : null}

        {!loading && !rows.length && (
          <Empty>
            {creditStatus === 'dead'
              ? 'Ölü ödeme yok — sağlayıcının bütün ödemeleri bize ulaşmış.'
              : 'Bu süzgeçle kupon bulunamadı.'}
          </Empty>
        )}

        {!loading && rows.length > 0 && (
          <>
            <p className="mb-2 text-[11px] text-slate-500">
              {total.toLocaleString('tr-TR')} kayıttan {rows.length} tanesi.
            </p>
            <div className="overflow-x-auto">
              <table className="w-full text-[11px]">
                <thead className="text-left text-[10px] uppercase tracking-wide text-slate-500">
                  <tr>
                    <th className="py-1">Kupon</th>
                    <th>Oyuncu</th>
                    <th>Bahis</th>
                    <th>Ödeme</th>
                    <th>Durum</th>
                    <th>Ödeme durumu</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => {
                    const id = String(row.bet_id ?? row.id ?? '');
                    const credit = String(row.credit_status || 'none');
                    return (
                      <tr key={id} className="border-t border-slate-800">
                        <td className="py-1 font-semibold text-white">#{id}</td>
                        <td className="text-slate-400">
                          {row.external_player_id || row.player || '—'}
                        </td>
                        <td>{formatMoney(number(row.stake))}</td>
                        <td>{formatMoney(number(row.payout))}</td>
                        <td className="text-slate-400">{row.status || '—'}</td>
                        <td className={CREDIT_STATUS_TONE[credit] || ''}>
                          {CREDIT_STATUS_LABELS[credit] || credit}
                        </td>
                        <td className="text-right">
                          {canRetryCredit(credit) && (
                            <button
                              type="button"
                              className="admin-btn-primary inline-flex items-center gap-1 !py-1 !text-[10px]"
                              disabled={busy === id}
                              onClick={() => retry(row)}
                            >
                              {busy === id
                                ? <Loader2 size={11} className="animate-spin" />
                                : <RotateCw size={11} />}
                              Yeniden kuyruğa al
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}
      </Panel>
    </div>
  );
};

// --------------------------------------------------------------------------- #
// Bonus & freebet
// --------------------------------------------------------------------------- #
const BonusTab = () => {
  const { toast } = useToast();
  const [offers, setOffers] = useState([]);
  const [grants, setGrants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [problem, setProblem] = useState('');
  const [busy, setBusy] = useState('');

  // Yeni kampanya formu.
  const [form, setForm] = useState({
    type: 'freebet', name: '', description: '', amount: '',
    min_odds: '', expiry_days: '7', wagering_multiplier: '1.0',
    max_grants_per_player: '1', max_total_grants: '1000',
  });
  // Hak tanıma formu.
  const [grant, setGrant] = useState({
    offer_id: '', username: '', external_player_id: '', amount: '',
  });

  const load = useCallback(async () => {
    setLoading(true);
    setProblem('');
    try {
      const [offersRes, grantsRes] = await Promise.all([
        partnerApi.bonusOffers({}).catch(() => ({ data: {} })),
        partnerApi.bonusGrants({ limit: 50 }).catch(() => ({ data: {} })),
      ]);
      const rows = offersRes.data?.offers || offersRes.data?.items || [];
      setOffers(Array.isArray(rows) ? rows : []);
      const grantRows = grantsRes.data?.grants || grantsRes.data?.items || [];
      setGrants(Array.isArray(grantRows) ? grantRows : []);
    } catch (error) {
      setProblem(errorText(error, 'Bonus listesi alınamadı'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const setField = (key, value) => setForm((current) => ({ ...current, [key]: value }));
  const setGrantField = (key, value) => setGrant((current) => ({ ...current, [key]: value }));

  const createOffer = async () => {
    if (!form.name.trim() || !form.amount) {
      toast({ title: 'Kampanya adı ve tutarı gerekli', variant: 'destructive' });
      return;
    }
    setBusy('offer');
    try {
      const body = {
        type: form.type,
        name: form.name.trim(),
        description: form.description.trim() || undefined,
        amount: Number(form.amount),
        currency: 'TRY',
        wagering_multiplier: number(form.wagering_multiplier),
        expiry_days: Number(form.expiry_days),
        max_grants_per_player: Number(form.max_grants_per_player),
        max_total_grants: Number(form.max_total_grants),
      };
      if (form.min_odds) body.min_odds = Number(form.min_odds);
      await partnerApi.createOffer(body);
      toast({ title: 'Kampanya tanımlandı' });
      setForm({ ...form, name: '', description: '', amount: '', min_odds: '' });
      await load();
    } catch (error) {
      toast({
        title: 'Kampanya tanımlanamadı',
        description: errorText(error),
        variant: 'destructive',
      });
    } finally {
      setBusy('');
    }
  };

  const grantBonus = async () => {
    const offerId = Number(grant.offer_id);
    const amount = Number(grant.amount);
    const player = grant.username.trim() || grant.external_player_id.trim();
    if (!offerId || !amount || !player) {
      toast({
        title: 'Kampanya, tutar ve oyuncu gerekli',
        variant: 'destructive',
      });
      return;
    }
    setBusy('grant');
    try {
      const body = {
        offer_id: offerId,
        amount,
        granted_by: 'admin',
        source_ref: `admin-${Date.now()}`,
      };
      if (grant.username.trim()) body.username = grant.username.trim();
      else body.external_player_id = grant.external_player_id.trim();
      const { data } = await partnerApi.grantBonus(body);
      toast({
        title: 'Hak tanımlandı',
        description:
          `Grant #${data?.grant?.id || '—'} · durum `
          + `${data?.grant?.status || '—'}.`,
      });
      setGrant({ ...grant, username: '', external_player_id: '', amount: '' });
      await load();
    } catch (error) {
      toast({
        title: 'Hak tanımlanamadı',
        description: errorText(error),
        variant: 'destructive',
      });
    } finally {
      setBusy('');
    }
  };

  const cancelGrant = async (row) => {
    const id = String(row.id ?? row.grant_id ?? '');
    setBusy(`cancel-${id}`);
    try {
      await partnerApi.cancelGrant(id);
      toast({ title: 'Hak iptal edildi' });
      await load();
    } catch (error) {
      toast({
        title: 'Hak iptal edilemedi',
        description: errorText(error),
        variant: 'destructive',
      });
    } finally {
      setBusy('');
    }
  };

  if (loading) return <Busy />;

  return (
    <div className="space-y-4">
      <Problem>{problem}</Problem>

      <Panel
        title="Kampanyalar (offers)"
        description={
          'Freebet, yatırım eşleşmesi, cashback ya da kayıp iadesi kampanyası. '
          + 'Kapsam imzadan gelir — başka bir markanın kampanyasını ne görürsünüz '
          + 'ne de değiştirirsiniz. Serbest bahiste oyuncu kazandığında cüzdan '
          + 'bildirimi yalnızca net kârı taşır (bkz. freebet sözleşmesi).'
        }
      >
        {!offers.length ? (
          <Empty>Tanımlı kampanya yok.</Empty>
        ) : (
          <ul className="space-y-1">
            {offers.slice(0, 50).map((row) => (
              <li
                key={row.id}
                className="flex flex-wrap items-center gap-2 rounded border border-slate-800 bg-slate-950/40 p-2 text-[11px]"
              >
                <span className="font-semibold text-white">{row.name || row.id}</span>
                <span className="text-slate-500">#{row.id}</span>
                <span className="text-slate-400">{row.type || '—'}</span>
                <span>{formatMoney(number(row.amount))}</span>
                <span className="text-slate-500">
                  {row.total_grants_count != null
                    ? `tanımlanan: ${row.total_grants_count}` : ''}
                </span>
                {row.is_active === false && (
                  <span className="text-amber-300">kapalı</span>
                )}
              </li>
            ))}
          </ul>
        )}

        <div className="mt-4 border-t border-slate-800 pt-3">
          <div className="mb-2 text-[10px] font-semibold uppercase tracking-wide text-slate-500">
            Yeni kampanya
          </div>
          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
            <Field label="Tür">
              <select
                className="admin-input"
                value={form.type}
                onChange={(event) => setField('type', event.target.value)}
              >
                {BONUS_TYPES.map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </Field>
            <Field label="Ad">
              <input
                className="admin-input"
                value={form.name}
                onChange={(event) => setField('name', event.target.value)}
                placeholder="Hoş geldin freebeti"
              />
            </Field>
            <Field label="Tutar">
              <input
                className="admin-input"
                value={form.amount}
                onChange={(event) => setField('amount', event.target.value)}
                placeholder="10.00"
              />
            </Field>
            <Field label="Asgari oran (isteğe bağlı)">
              <input
                className="admin-input"
                value={form.min_odds}
                onChange={(event) => setField('min_odds', event.target.value)}
                placeholder="1.50"
              />
            </Field>
            <Field label="Geçerlilik (gün)">
              <input
                className="admin-input"
                value={form.expiry_days}
                onChange={(event) => setField('expiry_days', event.target.value)}
              />
            </Field>
            <Field label="Oyuncu başına hak">
              <input
                className="admin-input"
                value={form.max_grants_per_player}
                onChange={(event) => setField('max_grants_per_player', event.target.value)}
              />
            </Field>
            <Field label="Toplam kota">
              <input
                className="admin-input"
                value={form.max_total_grants}
                onChange={(event) => setField('max_total_grants', event.target.value)}
              />
            </Field>
          </div>
          <button
            type="button"
            className="admin-btn-primary mt-3 inline-flex items-center gap-2 !py-1 !text-[11px]"
            disabled={busy === 'offer'}
            onClick={createOffer}
          >
            {busy === 'offer' ? <Loader2 size={12} className="animate-spin" /> : <Gift size={12} />}
            Kampanya tanımla
          </button>
        </div>
      </Panel>

      <Panel
        title="Haklar (grants)"
        description={
          'Bir kampanyayı oyuncuya tanımlar. Kullanıcı adını yazarsanız '
          + 'external_player_id bizde çözülür; doğrudan kimlik de '
          + 'yazabilirsiniz. `source_ref` tekilleştirme içindir — aynı referans '
          + 'ikinci kez gelirse mevcut hak döner, yeni hak açılmaz.'
        }
      >
        {!grants.length ? (
          <Empty>Tanımlı hak yok.</Empty>
        ) : (
          <ul className="space-y-1">
            {grants.slice(0, 50).map((row) => (
              <li
                key={row.id}
                className="flex flex-wrap items-center gap-2 rounded border border-slate-800 bg-slate-950/40 p-2 text-[11px]"
              >
                <span className="font-semibold text-white">#{row.id}</span>
                <span className="text-slate-400">{row.offer_name || `offer #${row.offer_id}`}</span>
                <span className="text-slate-400">{row.type || '—'}</span>
                <span>{formatMoney(number(row.amount))}</span>
                <span className="text-slate-500">{row.status || '—'}</span>
                {row.external_player_id && (
                  <span className="text-slate-500">{row.external_player_id}</span>
                )}
                {row.status === 'active' && (
                  <button
                    type="button"
                    className="admin-btn-secondary ml-auto !py-1 !text-[10px]"
                    disabled={busy === `cancel-${row.id}`}
                    onClick={() => cancelGrant(row)}
                  >
                    İptal et
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}

        <div className="mt-4 border-t border-slate-800 pt-3">
          <div className="mb-2 text-[10px] font-semibold uppercase tracking-wide text-slate-500">
            Hak tanımla
          </div>
          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
            <Field label="Kampanya (offer_id)">
              <input
                className="admin-input"
                value={grant.offer_id}
                onChange={(event) => setGrantField('offer_id', event.target.value)}
                placeholder="1"
              />
            </Field>
            <Field label="Tutar">
              <input
                className="admin-input"
                value={grant.amount}
                onChange={(event) => setGrantField('amount', event.target.value)}
                placeholder="10.00"
              />
            </Field>
            <Field label="Kullanıcı adı" hint="Kimlik yoksa kullanıcı adından çözülür">
              <input
                className="admin-input"
                value={grant.username}
                onChange={(event) => setGrantField('username', event.target.value)}
              />
            </Field>
            <Field label="external_player_id">
              <input
                className="admin-input"
                value={grant.external_player_id}
                onChange={(event) => setGrantField('external_player_id', event.target.value)}
              />
            </Field>
          </div>
          <button
            type="button"
            className="admin-btn-primary mt-3 inline-flex items-center gap-2 !py-1 !text-[11px]"
            disabled={busy === 'grant'}
            onClick={grantBonus}
          >
            {busy === 'grant' ? <Loader2 size={12} className="animate-spin" /> : <Gift size={12} />}
            Hak tanımla
          </button>
        </div>
      </Panel>
    </div>
  );
};

// --------------------------------------------------------------------------- #
// Risk & limitler
// --------------------------------------------------------------------------- #
const RiskTab = () => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [problem, setProblem] = useState('');
  const [busy, setBusy] = useState('');
  const [risk, setRisk] = useState({});
  const [ignored, setIgnored] = useState([]);
  const [rules, setRules] = useState([]);
  const [audit, setAudit] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setProblem('');
    try {
      const [config, ruleRes, auditRes] = await Promise.all([
        partnerApi.config(),
        partnerApi.riskRules().catch(() => ({ data: {} })),
        partnerApi.riskAudit().catch(() => ({ data: null })),
      ]);
      setRisk(config.data?.risk || {});
      const list = ruleRes.data?.rules || ruleRes.data?.items || [];
      setRules(Array.isArray(list) ? list : []);
      setAudit(auditRes.data || null);
    } catch (error) {
      setProblem(errorText(error, 'Risk yapılandırması alınamadı'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setBusy('config');
    try {
      const { data } = await partnerApi.saveRiskConfig(risk);
      setIgnored(ignoredFields(data));
      toast({ title: 'Risk yapılandırması kaydedildi' });
      await load();
    } catch (error) {
      toast({
        title: 'Kaydedilemedi',
        description: errorText(error),
        variant: 'destructive',
      });
    } finally {
      setBusy('');
    }
  };

  if (loading) return <Busy />;

  return (
    <div className="space-y-4">
      <Problem>{problem}</Problem>

      <Panel
        title="Risk yapılandırması"
        description={
          'Katmanlı risk limitleri (genel, tekli, canlı, ülke başına, '
          + 'oyuncu/maç bayrakları). Gövde değiştirildiği gibi eksiksiz geri '
          + 'gönderilir — sağlayıcı birleştirmez. Tanınmayan bir alan sessizce '
          + 'atlanır; cevaptaki uygulanmayanlar listesi aşağıda görünür.'
        }
      >
        <JsonEditor value={risk} onChange={setRisk} rows={16} />
        <button
          type="button"
          className="admin-btn-primary mt-2 inline-flex items-center gap-2 !py-1 !text-[11px]"
          disabled={busy === 'config'}
          onClick={save}
        >
          {busy === 'config' ? <Loader2 size={12} className="animate-spin" /> : <ShieldAlert size={12} />}
          Risk yapılandırmasını kaydet
        </button>
        <IgnoredFields fields={ignored} />
      </Panel>

      <Panel
        title="Kapsamlı risk kuralları"
        description="Merchant/oyuncu/ülke/spor/lig/maç/market kapsamında kurallar. Hub'daki kurallarla aynı listedir; silme buradan da yapılabilir."
      >
        {!rules.length ? (
          <Empty>Kural yok.</Empty>
        ) : (
          <ul className="space-y-1">
            {rules.slice(0, 100).map((row, index) => (
              <li
                key={row.id || index}
                className="flex flex-wrap items-center gap-2 rounded border border-slate-800 bg-slate-950/40 p-2 text-[11px] text-slate-300"
              >
                <span className="font-semibold text-white">#{row.id || index}</span>
                {row.scope && <span>{row.scope}</span>}
                {row.scope_id && <span className="text-slate-500">{row.scope_id}</span>}
                {row.rule && <span className="text-slate-400">{JSON.stringify(row.rule).slice(0, 120)}</span>}
                <button
                  type="button"
                  className="admin-btn-secondary ml-auto !py-1 !text-[10px]"
                  onClick={async () => {
                    try {
                      await partnerApi.deleteRiskRule(String(row.id ?? row.rule_id ?? ''));
                      toast({ title: 'Kural silindi' });
                      await load();
                    } catch (error) {
                      toast({
                        title: 'Silinemedi',
                        description: errorText(error),
                        variant: 'destructive',
                      });
                    }
                  }}
                >
                  Sil
                </button>
              </li>
            ))}
          </ul>
        )}
      </Panel>

      {audit && (
        <Panel title="Risk karar izi" description="Risk kararlarının salt okunur kaydı.">
          <JsonEditor value={audit} onChange={() => {}} rows={8} hint="Salt okunur — düzenleme kaydedilmez." />
        </Panel>
      )}
    </div>
  );
};

// --------------------------------------------------------------------------- #
// Görünürlük & cashout
// --------------------------------------------------------------------------- #
const VisibilityTab = () => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [problem, setProblem] = useState('');
  const [visibility, setVisibility] = useState({});
  const [cashout, setCashout] = useState({});
  const [settings, setSettings] = useState({});
  const [busy, setBusy] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setProblem('');
    try {
      const [config, cashoutRes, settingsRes] = await Promise.all([
        partnerApi.config(),
        partnerApi.cashout().catch(() => ({ data: {} })),
        partnerApi.settings().catch(() => ({ data: {} })),
      ]);
      setVisibility(config.data?.visibility || {});
      setCashout(cashoutRes.data?.cashout || cashoutRes.data || {});
      setSettings(settingsRes.data || {});
    } catch (error) {
      setProblem(errorText(error, 'Görünürlük alınamadı'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async (kind) => {
    setBusy(kind);
    try {
      if (kind === 'visibility') await partnerApi.saveVisibility(visibility);
      if (kind === 'cashout') await partnerApi.saveCashout(cashout);
      if (kind === 'settings') await partnerApi.saveSettings(settings);
      toast({ title: 'Kaydedildi' });
      await load();
    } catch (error) {
      toast({
        title: 'Kaydedilemedi',
        description: errorText(error),
        variant: 'destructive',
      });
    } finally {
      setBusy('');
    }
  };

  if (loading) return <Busy />;

  return (
    <div className="space-y-4">
      <Problem>{problem}</Problem>

      <Panel
        title="Görünürlük"
        description={
          'Gizlenen spor/ülke/lig/maç/market kümesi. Gövdenin tamamı yazılır — '
          + 'sağlayıcı bu ucu birleştirmez, değiştirir.'
        }
      >
        <JsonEditor
          value={visibility}
          onChange={setVisibility}
          rows={12}
          hint='Örnek: {"sports":{"off":[3]},"tournaments":{"off":[4520]}}'
        />
        <button
          type="button"
          className="admin-btn-primary mt-2 inline-flex items-center gap-2 !py-1 !text-[11px]"
          disabled={busy === 'visibility'}
          onClick={() => save('visibility')}
        >
          Görünürlüğü kaydet
        </button>
      </Panel>

      <Panel
        title="Erken kapatma (cashout)"
        description={
          'Açık/kapalı, canlı, parçalı ve otomatik cashout. Parçalı ve otomatik '
          + 'varsayılan kapalıdır; freebet ve sistem kuponunda cashout yoktur.'
        }
      >
        <JsonEditor
          value={cashout}
          onChange={setCashout}
          rows={8}
          hint='Örnek: {"enabled":true,"live":true,"partial_enabled":false,"auto_enabled":false}'
        />
        <button
          type="button"
          className="admin-btn-primary mt-2 inline-flex items-center gap-2 !py-1 !text-[11px]"
          disabled={busy === 'cashout'}
          onClick={() => save('cashout')}
        >
          Cashout ayarını kaydet
        </button>
      </Panel>

      <Panel
        title="Ayarlar"
        description={
          'Diller, bahis türleri, para birimleri, bildirim ve cashout anahtarları. '
          + 'Gövde **birleştirilir**: yalnızca gönderilen üst düzey anahtarlar değişir.'
        }
      >
        <JsonEditor
          value={settings}
          onChange={setSettings}
          rows={8}
          hint='Örnek: {"default_language":"tr","languages":["tr","en"]}'
        />
        <button
          type="button"
          className="admin-btn-primary mt-2 inline-flex items-center gap-2 !py-1 !text-[11px]"
          disabled={busy === 'settings'}
          onClick={() => save('settings')}
        >
          Ayarları kaydet
        </button>
      </Panel>
    </div>
  );
};

// --------------------------------------------------------------------------- #
// Görünüm
// --------------------------------------------------------------------------- #
const AppearanceTab = () => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [problem, setProblem] = useState('');
  const [theme, setTheme] = useState({});
  const [presets, setPresets] = useState([]);
  const [busy, setBusy] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setProblem('');
    try {
      const [themeRes, presetRes] = await Promise.all([
        partnerApi.theme(),
        partnerApi.themePresets().catch(() => ({ data: {} })),
      ]);
      setTheme(themeRes.data || {});
      const rows = presetRes.data?.themes || presetRes.data?.items || [];
      setPresets(Array.isArray(rows) ? rows : []);
    } catch (error) {
      setProblem(errorText(error, 'Tema alınamadı'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setBusy('theme');
    try {
      await partnerApi.saveTheme(theme);
      toast({ title: 'Tema kaydedildi' });
      await load();
    } catch (error) {
      toast({
        title: 'Kaydedilemedi',
        description: errorText(error),
        variant: 'destructive',
      });
    } finally {
      setBusy('');
    }
  };

  if (loading) return <Busy />;

  return (
    <div className="space-y-4">
      <Problem>{problem}</Problem>

      <Panel
        title="Hazır şemalar"
        description="Sağlayıcının hazır tema listesi. Hazır şema, özel üç renk ya da ek CSS'ten yalnızca biri etkindir."
      >
        {!presets.length ? (
          <Empty>Şema listesi boş.</Empty>
        ) : (
          <ul className="flex flex-wrap gap-1">
            {presets.slice(0, 40).map((row) => (
              <li
                key={row.id || row}
                className="rounded border border-slate-800 bg-slate-950/40 px-2 py-1 text-[11px] text-slate-300"
              >
                {row.id || row}
              </li>
            ))}
          </ul>
        )}
      </Panel>

      <Panel
        title="Tema"
        description={
          'Hazır şema (`{"theme":"flexrix-red"}`), özel üç renk '
          + '(`{"scheme":{"b":"#…","hero":"#…","accent":"#…"}}`) ya da ek CSS '
          + '(`{"css":"…"}`). Sağlayıcı tanımadığı gövdeyi 422 ile reddeder.'
        }
      >
        <JsonEditor value={theme} onChange={setTheme} rows={10} />
        <button
          type="button"
          className="admin-btn-primary mt-2 inline-flex items-center gap-2 !py-1 !text-[11px]"
          disabled={busy === 'theme'}
          onClick={save}
        >
          Temayı kaydet
        </button>
      </Panel>
    </div>
  );
};

// --------------------------------------------------------------------------- #
// Trading & markalar
// --------------------------------------------------------------------------- #
const TradingTab = () => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [problem, setProblem] = useState('');
  const [live, setLive] = useState([]);
  const [overrides, setOverrides] = useState([]);
  const [brands, setBrands] = useState([]);
  const [draft, setDraft] = useState({
    scope: 'outcome', outcome_id: '', market_id: '', fixture_id: '',
    league_id: '', action: 'margin', margin_pct: '',
  });
  const [bulkFixture, setBulkFixture] = useState('');
  const [busy, setBusy] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setProblem('');
    try {
      const [liveRes, overRes, brandsRes] = await Promise.all([
        partnerApi.tradingLive().catch(() => ({ data: {} })),
        partnerApi.overrides().catch(() => ({ data: {} })),
        partnerApi.brands().catch(() => ({ data: {} })),
      ]);
      setLive(liveRes.data?.games || liveRes.data?.items || []);
      setOverrides(overRes.data?.overrides || overRes.data?.items || []);
      const rows = brandsRes.data?.brands || brandsRes.data?.items || [];
      setBrands(Array.isArray(rows) ? rows : []);
    } catch (error) {
      setProblem(errorText(error, 'Trading verisi alınamadı'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const createOverride = async () => {
    const scopeId = draft[`${draft.scope}_id`];
    if (!scopeId) {
      toast({ title: 'Kapsam kimliği gerekli', variant: 'destructive' });
      return;
    }
    setBusy('override');
    try {
      const body = { scope: draft.scope, action: draft.action };
      body[`${draft.scope}_id`] = scopeId;
      if (draft.action === 'margin') body.margin_pct = Number(draft.margin_pct);
      await partnerApi.saveOverride(body);
      toast({ title: 'Oran müdahalesi eklendi' });
      await load();
    } catch (error) {
      toast({
        title: 'Eklenemedi',
        description: errorText(error),
        variant: 'destructive',
      });
    } finally {
      setBusy('');
    }
  };

  const bulk = async () => {
    if (!bulkFixture) {
      toast({ title: 'Maç kimliği gerekli', variant: 'destructive' });
      return;
    }
    setBusy('bulk');
    try {
      await partnerApi.tradingBulk({ fixture_id: bulkFixture, action: 'suspend' });
      toast({ title: 'Maç askıya alındı' });
      await load();
    } catch (error) {
      toast({
        title: 'İşlem başarısız',
        description: errorText(error),
        variant: 'destructive',
      });
    } finally {
      setBusy('');
    }
  };

  if (loading) return <Busy />;

  return (
    <div className="space-y-4">
      <Problem>{problem}</Problem>

      <Panel
        title="Canlı maçlar"
        description="Trading'e konu olabilecek canlı maçlar (dakika, skor)."
      >
        {!live.length ? (
          <Empty>Canlı maç yok.</Empty>
        ) : (
          <ul className="space-y-1">
            {live.slice(0, 30).map((row) => (
              <li
                key={row.fixture_id || row.id}
                className="rounded border border-slate-800 bg-slate-950/40 p-2 text-[11px] text-slate-300"
              >
                <span className="font-semibold text-white">#{row.fixture_id}</span>{' '}
                {row.home} - {row.away} · {row.home_score}-{row.away_score} ·{' '}
                {row.clock || '—'}
              </li>
            ))}
          </ul>
        )}
      </Panel>

      <Panel
        title="Oran müdahaleleri"
        description={
          'Oran kuralı hem vitrini hem kabul edilen bahsi etkiler. Yayılma bir '
          + 'önbellek turu kadar gecikir (≤ 20 sn).'
        }
      >
        {!overrides.length ? (
          <Empty>Kural yok.</Empty>
        ) : (
          <ul className="space-y-1">
            {overrides.slice(0, 100).map((row, index) => (
              <li
                key={row.id || index}
                className="flex flex-wrap items-center gap-2 rounded border border-slate-800 bg-slate-950/40 p-2 text-[11px] text-slate-300"
              >
                <span className="font-semibold text-white">#{row.id || index}</span>
                <span>{row.scope}</span>
                <span className="text-slate-500">{row.scope_id || row.fixture_id || '—'}</span>
                <span>{row.action}</span>
                {row.margin_pct != null && <span>%{row.margin_pct}</span>}
                {row.fixed_odds != null && <span>@ {row.fixed_odds}</span>}
                <button
                  type="button"
                  className="admin-btn-secondary ml-auto !py-1 !text-[10px]"
                  onClick={async () => {
                    try {
                      await partnerApi.deleteOverride(String(row.id ?? row.rule_id ?? ''));
                      toast({ title: 'Kural silindi' });
                      await load();
                    } catch (error) {
                      toast({
                        title: 'Silinemedi',
                        description: errorText(error),
                        variant: 'destructive',
                      });
                    }
                  }}
                >
                  Sil
                </button>
              </li>
            ))}
          </ul>
        )}

        <div className="mt-4 border-t border-slate-800 pt-3">
          <div className="mb-2 text-[10px] font-semibold uppercase tracking-wide text-slate-500">
            Yeni oran müdahalesi
          </div>
          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
            <Field label="Kapsam">
              <select
                className="admin-input"
                value={draft.scope}
                onChange={(event) => setDraft({ ...draft, scope: event.target.value })}
              >
                <option value="outcome">Outcome</option>
                <option value="market">Market (tür)</option>
                <option value="fixture">Maç</option>
                <option value="league">Lig</option>
              </select>
            </Field>
            <Field label={`${draft.scope}_id`}>
              <input
                className="admin-input"
                value={draft[`${draft.scope}_id`]}
                onChange={(event) => setDraft({ ...draft, [`${draft.scope}_id`]: event.target.value })}
              />
            </Field>
            <Field label="Eylem">
              <select
                className="admin-input"
                value={draft.action}
                onChange={(event) => setDraft({ ...draft, action: event.target.value })}
              >
                <option value="margin">Marj</option>
                <option value="suspend">Askıya al</option>
                <option value="hide">Gizle</option>
              </select>
            </Field>
            {draft.action === 'margin' && (
              <Field label="margin_pct (-90…90)">
                <input
                  className="admin-input"
                  value={draft.margin_pct}
                  onChange={(event) => setDraft({ ...draft, margin_pct: event.target.value })}
                  placeholder="10"
                />
              </Field>
            )}
          </div>
          <button
            type="button"
            className="admin-btn-primary mt-3 inline-flex items-center gap-2 !py-1 !text-[11px]"
            disabled={busy === 'override'}
            onClick={createOverride}
          >
            Kuralı ekle
          </button>
        </div>

        <div className="mt-4 border-t border-slate-800 pt-3">
          <div className="mb-2 text-[10px] font-semibold uppercase tracking-wide text-slate-500">
            Maçı toplu askıya al
          </div>
          <div className="flex flex-wrap items-end gap-2">
            <Field label="Maç kimliği (fixture_id)">
              <input
                className="admin-input admin-input-w-md"
                value={bulkFixture}
                onChange={(event) => setBulkFixture(event.target.value)}
              />
            </Field>
            <button
              type="button"
              className="admin-btn-secondary !py-1 !text-[11px]"
              disabled={busy === 'bulk'}
              onClick={bulk}
            >
              Askıya al
            </button>
          </div>
        </div>
      </Panel>

      <Panel
        title="Markalar (alt bayi)"
        description={
          'Tek entegrasyonda birden çok site. Sizin ayarlarınız varsayılandır; '
          + 'marka yalnızca gönderdiği alanları geçersiz kılar. Bir marka '
          + '`launch` adresine `&brand=<id>` ile seçilir.'
        }
      >
        {!brands.length ? (
          <Empty>Marka yok.</Empty>
        ) : (
          <ul className="space-y-1">
            {brands.slice(0, 50).map((row) => (
              <li
                key={row.id || row.brand_id}
                className="flex flex-wrap items-center gap-2 rounded border border-slate-800 bg-slate-950/40 p-2 text-[11px] text-slate-300"
              >
                <span className="font-semibold text-white">
                  {row.name || row.id || row.brand_id}
                </span>
                {row.id && <span className="text-slate-500">#{row.id}</span>}
                {row.active === false && <span className="text-amber-300">kapalı</span>}
                <button
                  type="button"
                  className="admin-btn-secondary ml-auto !py-1 !text-[10px]"
                  onClick={async () => {
                    try {
                      await partnerApi.deleteBrand(String(row.id ?? row.brand_id ?? ''));
                      toast({ title: 'Marka silindi' });
                      await load();
                    } catch (error) {
                      toast({
                        title: 'Silinemedi',
                        description: errorText(error),
                        variant: 'destructive',
                      });
                    }
                  }}
                >
                  Sil
                </button>
              </li>
            ))}
          </ul>
        )}
      </Panel>
    </div>
  );
};

// --------------------------------------------------------------------------- #
// Webhook & anahtar
// --------------------------------------------------------------------------- #
const WebhookTab = () => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [problem, setProblem] = useState('');
  const [webhooks, setWebhooks] = useState({});
  const [queue, setQueue] = useState([]);
  const [channel, setChannel] = useState({});
  const [confirmRotate, setConfirmRotate] = useState(false);
  const [busy, setBusy] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setProblem('');
    try {
      const [hooksRes, queueRes, channelRes] = await Promise.all([
        partnerApi.webhooks(),
        partnerApi.webhookQueue().catch(() => ({ data: {} })),
        partnerApi.alertChannel().catch(() => ({ data: {} })),
      ]);
      setWebhooks(hooksRes.data || {});
      setQueue(queueRes.data?.items || queueRes.data?.queue || []);
      setChannel(channelRes.data || {});
    } catch (error) {
      setProblem(errorText(error, 'Webhook ayarları alınamadı'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const rotate = async () => {
    setBusy('rotate');
    try {
      await partnerApi.rotateKey();
      toast({
        title: 'API secret yenilendi',
        description: 'Yeni sır kaydedildi; eski sır kısa bir süre daha kabul edilir.',
      });
      setConfirmRotate(false);
    } catch (error) {
      toast({
        title: 'Anahtar yenilenemedi',
        description: errorText(error),
        variant: 'destructive',
      });
    } finally {
      setBusy('');
    }
  };

  if (loading) return <Busy />;

  return (
    <div className="space-y-4">
      <Problem>{problem}</Problem>

      <Panel
        title="Olay webhook'ları"
        description={
          'bet_placed / bet_settled / bet_cashed_out olaylarının bildirildiği '
          + 'adres ve ayrı webhook sırrı. `requires_prebet_validation` açılmadan '
          + 'önce cüzdan ucunun `validate_bet`\'i karşıladığından emin olun — '
          + 'hata dönen bir onay ucu her bahsi reddeder.'
        }
      >
        <JsonEditor
          value={webhooks}
          onChange={setWebhooks}
          rows={8}
          hint='Örnek: {"webhook_url":"https://siteniz.com/hook","events":["bet_settled"]}'
        />
        <button
          type="button"
          className="admin-btn-primary mt-2 inline-flex items-center gap-2 !py-1 !text-[11px]"
          disabled={busy === 'hooks'}
          onClick={async () => {
            setBusy('hooks');
            try {
              await partnerApi.saveWebhooks(webhooks);
              toast({ title: 'Webhook ayarları kaydedildi' });
              await load();
            } catch (error) {
              toast({
                title: 'Kaydedilemedi',
                description: errorText(error),
                variant: 'destructive',
              });
            } finally {
              setBusy('');
            }
          }}
        >
          Webhook ayarlarını kaydet
        </button>

        {queue.length > 0 && (
          <div className="mt-3">
            <div className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-slate-500">
              Kuyruk
            </div>
            <ul className="space-y-1">
              {queue.slice(0, 20).map((row, index) => (
                <li
                  key={row.id || index}
                  className="rounded border border-slate-800 bg-slate-950/40 p-2 text-[11px] text-slate-300"
                >
                  {row.event || 'olay'} · {row.status || '—'}
                  {row.bet_id ? ` · kupon #${row.bet_id}` : ''}
                </li>
              ))}
            </ul>
          </div>
        )}
      </Panel>

      <Panel
        title="Uyarı kanalı"
        description={
          'Sağlayıcı bir uyarı ürettiğinde (ölü ödeme, takılan sonuç) sizi '
          + 'nereden haberdar edeceği. Telegram jetonu okunurken geri dönmez; '
          + 'yalnızca tanımlı olup olmadığı bildirilir.'
        }
      >
        <JsonEditor
          value={channel}
          onChange={setChannel}
          rows={7}
          hint='Örnek: {"telegram_chat":"@kanal","min_severity":"warn"}'
        />
        <button
          type="button"
          className="admin-btn-primary mt-2 inline-flex items-center gap-2 !py-1 !text-[11px]"
          disabled={busy === 'channel'}
          onClick={async () => {
            setBusy('channel');
            try {
              await partnerApi.saveAlertChannel(channel);
              toast({ title: 'Uyarı kanalı kaydedildi' });
              await load();
            } catch (error) {
              toast({
                title: 'Kaydedilemedi',
                description: errorText(error),
                variant: 'destructive',
              });
            } finally {
              setBusy('');
            }
          }}
        >
          Uyarı kanalını kaydet
        </button>
      </Panel>

      <Panel
        title="API secret yenileme"
        description={
          'Sağlayıcı yeni bir sır üretir ve **o andan itibaren onunla** '
          + 'imzalar. Yeni sır burada kendiliğinden kaydedilir; eski sır bir '
          + 'süre daha kabul edilmeye devam eder, yani açık kuponların '
          + 'bildirimleri düşmez. Sır panele hiçbir zaman geri dönmez.'
        }
      >
        {confirmRotate ? (
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-[11px] text-amber-300">
              Anahtar hemen değişecek. Sağlayıcı panelinde elle bir yere
              yazdığınız sır varsa geçersiz kalır.
            </span>
            <button
              type="button"
              className="admin-btn-danger inline-flex items-center gap-2 !py-1 !text-[11px]"
              disabled={busy === 'rotate'}
              onClick={rotate}
            >
              {busy === 'rotate'
                ? <Loader2 size={12} className="animate-spin" />
                : <KeyRound size={12} />}
              Evet, yenile
            </button>
            <button
              type="button"
              className="admin-btn-secondary !py-1 !text-[11px]"
              onClick={() => setConfirmRotate(false)}
            >
              Vazgeç
            </button>
          </div>
        ) : (
          <button
            type="button"
            className="admin-btn-secondary inline-flex items-center gap-2 !py-1 !text-[11px]"
            onClick={() => setConfirmRotate(true)}
          >
            <KeyRound size={12} /> API secret’i yenile
          </button>
        )}
      </Panel>
    </div>
  );
};

// --------------------------------------------------------------------------- #
// Kabuk
// --------------------------------------------------------------------------- #
const SportDigiManager = () => {
  const [tab, setTab] = useState('summary');

  const body = useMemo(() => {
    switch (tab) {
      case 'bets': return <BetsTab />;
      case 'bonus': return <BonusTab />;
      case 'risk': return <RiskTab />;
      case 'visibility': return <VisibilityTab />;
      case 'appearance': return <AppearanceTab />;
      case 'trading': return <TradingTab />;
      case 'webhooks': return <WebhookTab />;
      default: return <SummaryTab />;
    }
  }, [tab]);

  return (
    <div className="space-y-4 p-4">
      <header>
        <h1 className="text-lg font-bold text-white">Digitain Yönetimi</h1>
        <p className="mt-1 max-w-4xl text-[12px] leading-relaxed text-slate-400">
          Sağlayıcının self-servis yönetim yüzeyi (Partner API). Buradaki
          ayarların hiçbiri bizim veritabanımızda durmaz — hepsi doğrudan
          sağlayıcıya yazılır ve sayfa her açıldığında oradan okunur.
          Entegrasyonun kendi ayarları (merchant key, secret, callback onayı)
          <b className="text-slate-300"> Oyun Kontrol → Spor &amp; Cashout</b>
          {' '}altındadır.
        </p>
      </header>

      <nav className="flex flex-wrap gap-1">
        {TABS.map((item) => {
          const Icon = item.icon;
          const active = tab === item.id;
          return (
            <button
              key={item.id}
              type="button"
              onClick={() => setTab(item.id)}
              className={`inline-flex items-center gap-2 rounded-lg px-3 py-1.5 text-[11px] font-semibold transition ${
                active
                  ? 'bg-sky-500/15 text-sky-200'
                  : 'text-slate-400 hover:bg-slate-800/60 hover:text-slate-200'
              }`}
            >
              <Icon size={13} /> {item.label}
            </button>
          );
        })}
      </nav>

      {body}
    </div>
  );
};

export default SportDigiManager;
